import React from 'react';
import styled from 'styled-components';
import Modal, { ModalHeader, ModalScrollable } from 'components/modal';
import { useGenerateCode, lockDoorLabel, getTourLocks } from '../hooks/useGenerateCode';
import Icon from '../../../components/icon';
import Button from '../../../components/button';
import { format, parseISO, setMinutes } from 'date-fns';

const formatDate = (date) => {
  const parsedDate = parseISO(date || new Date().toISOString());
  const dateWithZeroMinutes = setMinutes(parsedDate, 0);
  return format(dateWithZeroMinutes, 'MM/d/yy h:mm a');
};

// A CodeBox lockbox code is a day code (code.codeDate, the property-local date), so it shows
// that date rather than an hour window. As CodeBox confirmed, it works only during the
// showing hours set on the box and stops 1–2 hours after it first opens the box — neither
// is readable through their API, so the wording stays general. Same wording as core's
// imports/aptly-access/codebox.js.
const isLockboxCode = (lock, code) => code?.provider === 'codebox' || lock?.provider === 'codebox';

const CodeDetails = ({ lock, code, showTitle }) => (
  <CodeBlock>
    {showTitle && <CodeTitle>{lockDoorLabel(lock)}</CodeTitle>}
    <div>
      <Label>Passcode: </Label>
      {code?.passcode}
    </div>
    {isLockboxCode(lock, code) ? (
      <>
        <div>
          <Label>Valid: </Label>
          {code?.codeDate}, during the lockbox&apos;s showing hours
        </div>
        <Hint>
          Enter the code on the lockbox keypad, open the lockbox and take the key. The code stops
          working 1–2 hours after it first opens the lockbox.
        </Hint>
      </>
    ) : (
      <div>
        <Label>Valid period: </Label>
        {code?.startDate && formatDate(code?.startDate)} -{' '}
        {code?.endDate && formatDate(code?.endDate)}
      </div>
    )}
  </CodeBlock>
);

// Doors whose code could not be issued, with a retry that only asks for those doors.
const DoorErrors = ({ failed, retrying, onRetry }) => (
  <DoorErrorBox>
    {failed.map(({ lock, reason }) => (
      <div key={lock?.lockId}>
        No code for {lockDoorLabel(lock).toLowerCase()}
        {reason ? ` — ${reason}` : ''}
      </div>
    ))}
    <RequestLink onClick={() => !retrying && onRetry()}>
      {retrying ? 'Retrying…' : `Try again for ${failed.length > 1 ? 'these doors' : 'this door'}`}
    </RequestLink>
  </DoorErrorBox>
);

export default ({ onClose, showing }) => {
  const {
    generateCode,
    accessCode,
    accessCodes,
    isMultiLock,
    isLoading,
    requestAdditionalCode,
    isRequestingAdditional,
    additionalCode,
    additionalCodes,
    failedDoors,
    additionalFailedDoors,
    retryFailedDoors,
    retryAdditionalFailedDoors,
    errorMessage
  } = useGenerateCode({ showing });

  // the NoKey app opens TTLock doors only — a tour of lockboxes alone gets no app pitch
  const onlyLockboxes = React.useMemo(() => {
    const doors = getTourLocks(showing?.location);
    return doors.length > 0 && doors.every((lock) => isLockboxCode(lock));
  }, [showing]);

  const [showReasonInput, setShowReasonInput] = React.useState(false);
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    generateCode();
  }, []);

  const handleRequestAdditionalCode = () => {
    if (reason.trim()) {
      requestAdditionalCode(reason.trim());
    }
  };

  return (
    <Modal open height="auto" width="30%" onClose={onClose}>
      <HeaderValue>
        {onlyLockboxes
          ? isMultiLock
            ? 'Lockbox codes'
            : 'Lockbox code'
          : isMultiLock
          ? 'Access codes'
          : 'Unit access code'}
      </HeaderValue>
      <ModalScrollable>
        {isLoading ? (
          <ContentHolder>
            <StyledLoader name="fa-duotone fa-spinner-third fa-spin" size={100} />
          </ContentHolder>
        ) : (
          <BodyWrapper>
            {accessCode ? (
              <>
                {accessCodes.map(({ lock, code }) => (
                  <CodeDetails key={lock?.lockId} lock={lock} code={code} showTitle={isMultiLock} />
                ))}

                {failedDoors.length > 0 && (
                  <DoorErrors
                    failed={failedDoors}
                    retrying={isLoading}
                    onRetry={retryFailedDoors}
                  />
                )}

                {/* Request Additional Code Section */}
                <AdditionalCodeSection>
                  {additionalCode ? (
                    <AdditionalCodeDisplay>
                      <AdditionalCodeTitle>
                        Additional Code{additionalCodes.length > 1 ? 's' : ''}
                      </AdditionalCodeTitle>
                      {additionalCodes.map(({ lock, code }) => (
                        <CodeDetails
                          key={lock?.lockId}
                          lock={lock}
                          code={code}
                          showTitle={isMultiLock}
                        />
                      ))}
                      {additionalFailedDoors.length > 0 && (
                        <DoorErrors
                          failed={additionalFailedDoors}
                          retrying={isRequestingAdditional}
                          onRetry={() => retryAdditionalFailedDoors(reason.trim())}
                        />
                      )}
                    </AdditionalCodeDisplay>
                  ) : showReasonInput ? (
                    <ReasonInputWrapper>
                      <ReasonLabel>Please tell us why you need another code:</ReasonLabel>
                      <ReasonTextarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter your reason here..."
                        rows={3}
                      />
                      <ButtonRow>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setShowReasonInput(false);
                            setReason('');
                          }}
                          disabled={isRequestingAdditional}>
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleRequestAdditionalCode}
                          disabled={!reason.trim() || isRequestingAdditional}
                          loading={isRequestingAdditional}>
                          Get Code
                        </Button>
                      </ButtonRow>
                    </ReasonInputWrapper>
                  ) : (
                    <RequestLink onClick={() => setShowReasonInput(true)}>
                      Need another code?
                    </RequestLink>
                  )}
                </AdditionalCodeSection>
              </>
            ) : isMultiLock && failedDoors.length > 0 ? (
              // every door failed — list each one with its reason and a retry
              <DoorErrors failed={failedDoors} retrying={isLoading} onRetry={retryFailedDoors} />
            ) : (
              <Label>{errorMessage || 'Make sure you are in front of the property.'}</Label>
            )}
            {!onlyLockboxes && (
              <FallbackBox>
                <FallbackTitle>
                  Having trouble using the access code? Try our mobile app.
                </FallbackTitle>
                <AppLinks>
                  <StoreLink
                    href="https://apps.apple.com/us/app/aptly-nokey/id6753088464"
                    target="_blank"
                    rel="noopener noreferrer">
                    <Badge src="/assets/appstore/ios.svg" alt="Download on the App Store" />
                  </StoreLink>
                  <StoreLink
                    href="https://play.google.com/store/apps/details?id=com.getaptly.tours"
                    target="_blank"
                    rel="noopener noreferrer">
                    <Badge src="/assets/appstore/android.svg" alt="Get it on Google Play" />
                  </StoreLink>
                </AppLinks>
              </FallbackBox>
            )}
          </BodyWrapper>
        )}
      </ModalScrollable>
    </Modal>
  );
};

const ContentHolder = styled.div`
  height: 110px;
  width: 100px;
  margin-left: auto;
  margin-right: auto;
`;

const Label = styled.span`
  font-weight: bold;
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 0.9em;
  opacity: 0.8;
`;

const StyledLoader = styled(Icon)`
  width: 100%;
  margin: auto;
  display: flex;
  font-size: 30px;
  flex-shrink: 0;
`;

const HeaderValue = styled(ModalHeader)`
  font-size: 1.25em;
`;
const BodyWrapper = styled.div`
  max-width: 600px;
`;
const Details = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  align-items: center;

  > div {
    display: flex;
    flex-flow: column;
    word-break: break-word;
    font-size: 1.13em;
    gap: 10px;
  }
`;
const ActionRowWrapper = styled.div`
  display: flex;
  flex-flow: column;
  word-break: break-word;
  font-size: 1.13em;
  border-top: 1px solid #e6e6e6;

  > div {
    padding: 10px 5px;
    border-bottom: 1px solid #e6e6e6;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    &:hover {
      background-color: #e6e6e6;
    }
  }
`;

const FallbackBox = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e6e6e6;
`;

const FallbackTitle = styled.div`
  margin-bottom: 6px;
  text-align: center;
`;

const AppLinks = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
`;

const StoreLink = styled.a`
  display: inline-block;
  text-decoration: none;
`;

const Badge = styled.img`
  height: 40px;
  display: block;
  width: auto;
`;

const AdditionalCodeSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e6e6e6;
`;

const RequestLink = styled.a`
  display: inline-block;
  color: var(--color-primary, #007cff);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ReasonInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReasonLabel = styled.label`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const ReasonTextarea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: var(--color-primary, #007cff);
    box-shadow: 0 0 0 2px rgba(0, 124, 255, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const AdditionalCodeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #f5f5f5;
  border: 2px dashed #152a34;
  border-radius: 8px;
`;

const AdditionalCodeTitle = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #152a34;
  margin-bottom: 4px;
`;

const CodeBlock = styled.div`
  & + & {
    margin-top: 12px;
  }
`;

const CodeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const DoorErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fdecea;
  color: #8a1c13;
  font-size: 13px;
`;
